import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useI18n } from '../i18n/index.tsx';
import { api } from '../api/client.ts';
import type { CredentialView } from '../api/types.ts';
import { Notice, Skeleton } from '../components/ui.tsx';
import { Brand } from '../components/Shell.tsx';

/**
 * The page a one-time link from the bot opens.
 *
 * No navigation, no session, no account. Somebody arriving here has a link
 * and nothing else — which is the whole point: their PanelTunnel identity is
 * a Telegram chat, sign-up is closed, and the dashboard's own URL would have
 * shown them a login form asking for a password they were never given.
 *
 * The link is spent by the request this page makes, so there is deliberately
 * no retry: a second attempt would fail, and offering a button that cannot
 * work is worse than saying plainly that the link is finished.
 */
export function Access({ token }: { token: string }): ReactNode {
  const { t } = useI18n();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'open'; credentials: readonly CredentialView[] }
    | { kind: 'gone' }
  >({ kind: 'loading' });

  useEffect(() => {
    void (async () => {
      try {
        setState({ kind: 'open', credentials: await api.redeemAccessLink(token) });
      } catch {
        // Expired, already used, or never existed. One answer for all three:
        // telling them apart would let somebody probe for live links.
        setState({ kind: 'gone' });
      }
    })();
  }, [token]);

  return (
    <main className="boot access">
      <Brand />
      {state.kind === 'loading' ? (
        <>
          <p>{t('access.loading')}</p>
          <Skeleton lines={3} />
        </>
      ) : null}
      {state.kind === 'gone' ? <Notice tone="warning">{t('access.expired')}</Notice> : null}
      {state.kind === 'open' ? (
        <>
          <h1>{t('access.title')}</h1>
          <Notice tone="warning">{t('access.body')}</Notice>
          {state.credentials.length === 0 ? (
            <p>{t('access.none')}</p>
          ) : (
            <dl>
              {state.credentials.map((credential, index) => (
                <div key={`${credential.kind}-${index}`}>
                  {credential.panelHost ? (
                    <>
                      <dt>{t('access.panelHost')}</dt>
                      <dd>
                        <code>{credential.panelHost}</code>
                      </dd>
                    </>
                  ) : null}
                  {credential.accessVia === 'entry-relay' && credential.accessThroughHost ? (
                    <>
                      <dt>{t('access.via')}</dt>
                      <dd>{t('access.viaEntryRelay', { host: credential.accessThroughHost })}</dd>
                    </>
                  ) : null}
                  <dt>{t('result.address')}</dt>
                  <dd>
                    {credential.url ? (
                      <a href={credential.url} rel="noreferrer noopener">
                        {credential.url}
                      </a>
                    ) : (
                      '—'
                    )}
                  </dd>
                  <dt>{t('result.username')}</dt>
                  <dd>
                    <code>{credential.username ?? '—'}</code>
                  </dd>
                  <dt>{t('result.password')}</dt>
                  <dd>
                    {credential.hasSecret === false ? (
                      <span>{t('access.notHeld')}</span>
                    ) : (
                      <code>{credential.value ?? '—'}</code>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <p>{t('result.credentialWarning')}</p>
        </>
      ) : null}
    </main>
  );
}
